import * as Ariakit from '@ariakit/react';
import { Button as LaunchpadButton } from '@launchpad-ui/components';
import type { ClassValue } from 'clsx';
import clsx from 'clsx';

import type { ButtonProps } from '../Button/Button';
import { Button as OriginalButton } from '../Button/Button';
import type { Props as ButtonIconProps } from '../ButtonIcon/ButtonIcon';
import { ButtonIcon } from '../ButtonIcon/ButtonIcon';
import { Text } from '../Text/Text';

import { hiddenMenuArrow, menuDivider, menuHeading, menuItemVariants, menuList } from './styles.css';

type Props = React.PropsWithChildren<Partial<Ariakit.MenuProviderProps>>;

type MenuComponent = React.FC<Props> & {
  Button: typeof Button;
  List: typeof List;
  Item: typeof Item;
  Divider: typeof Divider;
  Heading: typeof Heading;
  useStore: typeof Ariakit.useMenuStore;
  useContext: typeof Ariakit.useMenuContext;
};

export const Menu: MenuComponent = ({ children, ...props }: Props) => (
  <Ariakit.MenuProvider {...props}>{children}</Ariakit.MenuProvider>
);

export type MenuButtonProps = React.PropsWithChildren<{
  cssClass?: ClassValue | ClassValue[];
  useLaunchPadButton?: boolean;
}> &
  Omit<Ariakit.MenuButtonProps, 'store'> &
  Pick<ButtonProps, 'iconLeft' | 'iconRight'> & {
    emphasis?: ButtonProps['emphasis'];
    icon?: ButtonIconProps['icon'];
    kind?: ButtonProps['kind'];
    size?: ButtonProps['size'] | ButtonIconProps['size'];
  };

const Button: React.FC<Omit<MenuButtonProps, 'store'>> = ({ children, ...props }) => {
  const menu = Ariakit.useMenuContext();

  let Component;
  if (props.icon && !children) {
    Component = <ButtonIcon icon={props.icon} />;
  } else if (props.useLaunchPadButton) {
    Component = <LaunchpadButton variant="minimal" />;
  } else {
    Component = <OriginalButton />;
  }

  return (
    <Ariakit.MenuButton render={Component} store={menu} {...props}>
      {children}
    </Ariakit.MenuButton>
  );
};

type ListProps = React.PropsWithChildren<{
  cssClass?: ClassValue | ClassValue[];
  portal?: boolean;
  portalElement?: HTMLElement;
}> &
  Partial<Ariakit.MenuProps>;

const List: React.FC<ListProps> = ({ children, cssClass, portal, portalElement, ...props }) => {
  const menu = Ariakit.useMenuContext();

  return (
    <Ariakit.Menu
      store={menu}
      gutter={4}
      portal={portal}
      portalElement={portalElement}
      className={clsx(menuList, cssClass)}
      {...props}
    >
      {/*
			There is a bug in v0.2.17 of Ariakit where you need to have this arrow
			rendered or else positioning of the popover breaks. We render it, but hide
			it with CSS. This is an issue with anything using a popover coming from the
			floating-ui library.
			*/}
      <Ariakit.MenuArrow size={1} className={hiddenMenuArrow} />
      {children}
    </Ariakit.Menu>
  );
};

const Item: React.FC<Ariakit.MenuItemProps> = ({ children, ...props }) => (
  <Ariakit.MenuItem className={menuItemVariants({ selected: false })} {...props}>
    {typeof children === 'string' ? <Text>{children}</Text> : children}
  </Ariakit.MenuItem>
);

const Divider: React.FC<Ariakit.MenuSeparatorProps> = ({ children, ...props }) => (
  <Ariakit.MenuSeparator className={menuDivider} {...props}>
    {children}
  </Ariakit.MenuSeparator>
);

const Heading: React.FC<Ariakit.MenuHeadingProps> = ({ children, className, ...props }) => (
  <>
    <Ariakit.MenuHeading className={clsx(menuHeading, className)} {...props}>
      {children}
    </Ariakit.MenuHeading>
    <Divider />
  </>
);

Menu.Button = Button;
Menu.List = List;
Menu.Item = Item;
Menu.Divider = Divider;
Menu.Heading = Heading;
Menu.useStore = Ariakit.useMenuStore;
Menu.useContext = Ariakit.useMenuContext;
